import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";
import { ItinerantStopDraft } from "./ItinerantExhibitionPanel";
import { ItinerantDrawerConfig } from "./itinerant-drawer-types";

type StopFieldMap<T extends FieldValues> = {
  catalogNumber?: Path<T>;
  note?: Path<T>;
  exemplaireExpose?: Path<T>;
};

/**
 * Keeps the classic right-side form in sync with the selected itinerant stop
 * (catalog / exemplaire on the right; dedicated note stays on each left card).
 */
export function useItinerantStopSelection<T extends FieldValues>(
  itinerantConfig: ItinerantDrawerConfig | undefined,
  stops: ItinerantStopDraft[],
  setStops: Dispatch<SetStateAction<ItinerantStopDraft[]>>,
  form: UseFormReturn<T>,
  fieldMap: StopFieldMap<T>,
) {
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null);

  const readFormIntoStop = useCallback(
    (stop: ItinerantStopDraft): ItinerantStopDraft => {
      const values = form.getValues();
      return {
        ...stop,
        catalogNumber: fieldMap.catalogNumber
          ? String(values[fieldMap.catalogNumber] ?? "")
          : stop.catalogNumber,
        note: fieldMap.note
          ? String(values[fieldMap.note] ?? "")
          : stop.note,
        exemplaireExpose: fieldMap.exemplaireExpose
          ? String(values[fieldMap.exemplaireExpose] ?? "")
          : stop.exemplaireExpose,
      };
    },
    [form, fieldMap],
  );

  const writeStopIntoForm = useCallback(
    (stop: ItinerantStopDraft) => {
      if (fieldMap.catalogNumber) {
        form.setValue(
          fieldMap.catalogNumber,
          stop.catalogNumber as PathValue<T, Path<T>>,
        );
      }
      if (fieldMap.note) {
        form.setValue(fieldMap.note, stop.note as PathValue<T, Path<T>>);
      }
      if (fieldMap.exemplaireExpose) {
        form.setValue(
          fieldMap.exemplaireExpose,
          stop.exemplaireExpose as PathValue<T, Path<T>>,
        );
      }
    },
    [form, fieldMap],
  );

  useEffect(() => {
    if (!itinerantConfig || stops.length === 0) return;
    const stillThere =
      selectedStopId && stops.some((s) => s.id === selectedStopId);
    if (stillThere) return;
    const seed =
      stops.find(
        (s) => s.exhibition.id === itinerantConfig.seedExhibition.id,
      ) ?? stops[0];
    setSelectedStopId(seed.id);
    const hasStopData =
      Boolean(seed.catalogNumber) ||
      Boolean(seed.note) ||
      Boolean(seed.exemplaireExpose) ||
      Boolean(itinerantConfig.initialStops?.length);
    if (hasStopData) {
      writeStopIntoForm(seed);
    } else {
      setStops((prev) =>
        prev.map((s) => (s.id === seed.id ? readFormIntoStop(s) : s)),
      );
    }
  }, [
    itinerantConfig,
    stops,
    selectedStopId,
    writeStopIntoForm,
    readFormIntoStop,
    setStops,
  ]);

  const selectStop = useCallback(
    (stopId: string) => {
      if (!stopId || stopId === selectedStopId) return;

      // Functional update so a freshly added stop (same event tick) is kept
      let target: ItinerantStopDraft | undefined;
      setStops((prev) => {
        const flushed = selectedStopId
          ? prev.map((s) =>
              s.id === selectedStopId ? readFormIntoStop(s) : s,
            )
          : prev;
        target = flushed.find((s) => s.id === stopId);
        return flushed;
      });
      setSelectedStopId(stopId);
      if (target) writeStopIntoForm(target);
    },
    [selectedStopId, setStops, readFormIntoStop, writeStopIntoForm],
  );

  /** Flush form into selected stop and return the full stops list for sync. */
  const flushStopsForSubmit = useCallback((): ItinerantStopDraft[] => {
    if (!selectedStopId) return stops;
    return stops.map((s) =>
      s.id === selectedStopId ? readFormIntoStop(s) : s,
    );
  }, [selectedStopId, stops, readFormIntoStop]);

  return {
    selectedStopId,
    selectStop,
    flushStopsForSubmit,
  };
}
